import { memo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ImageResize from 'quill-image-resize-module-react'

import { Container } from './Styles'

Quill.register('modules/imageResize', ImageResize)

const defaultToolbar = [
  [{ header: [1, 2, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }, { align: [] }],
  ['link', 'image'],
  ['clean'],
]
const miniToolbar = [['bold', 'italic', 'underline']]

const Composed = forwardRef(({
  field,
  height,
  placeholder = '',
  readOnly = false,
  mini = false,
  hideToolbar = false,
  noPadding = false,
  fontSize,
  imageHandler,
  toolbar,
}, ref) => {
  const editorRef = useRef()

  useImperativeHandle(ref, () => ({
    getEditor: () => editorRef.current?.getEditor?.(),
  }))

  useEffect(() => {
    if (editorRef.current) editorRef.current.updater.enqueueForceUpdate(editorRef.current)
  }, [field, editorRef])

  // Register custom image handler after mount
  useEffect(() => {
    if (imageHandler && editorRef.current) {
      const quill = editorRef.current.getEditor()
      if (quill) {
        const quillToolbar = quill.getModule('toolbar')
        if (quillToolbar) {
          quillToolbar.addHandler('image', imageHandler)
        }
      }
    }
  }, [imageHandler])

  // Position the link tooltip directly below the sticky toolbar whenever it appears
  useEffect(() => {
    if (!editorRef.current) return undefined
    const quill = editorRef.current.getEditor()
    if (!quill) return undefined

    const container = quill.container
    const quillRoot = container?.closest('.quill')
    if (!quillRoot) return undefined

    const toolbarEl = quillRoot.querySelector('.ql-toolbar')
    const tooltip = container.querySelector('.ql-tooltip')
    if (!tooltip) return undefined

    const observer = new MutationObserver(() => {
      // When the tooltip becomes visible (Quill removes the 'ql-hidden' class)
      if (!tooltip.classList.contains('ql-hidden')) {
        if (toolbarEl) {
          const toolbarRect = toolbarEl.getBoundingClientRect()
          tooltip.style.position = 'fixed'
          tooltip.style.top = `${toolbarRect.bottom + 4}px`
          tooltip.style.left = `${toolbarRect.left}px`
          tooltip.style.right = 'auto'
          tooltip.style.width = `${toolbarRect.width}px`
          tooltip.style.transform = 'none'
        }
      }
    })

    observer.observe(tooltip, { attributes: true, attributeFilter: ['class', 'style'] })

    return () => observer.disconnect()
  }, [])

  const resolvedToolbar = toolbar || (mini ? miniToolbar : readOnly ? [] : defaultToolbar)

  return (
    <Container
      noToolbar={readOnly || hideToolbar}
      mini={mini}
      noPadding={noPadding}
      height={height}
      fontSize={fontSize}
    >
      <ReactQuill
        ref={editorRef}
        theme="snow"
        value={field.value}
        onChange={field.onChange}
        onKeyDown={field.onKeyDown}
        placeholder={placeholder}
        readOnly={readOnly}
        modules={{
          imageResize: {
            parchment: Quill.import('parchment'),
            modules: ['Resize', 'DisplaySize'],
            displayStyles: {
              backgroundColor: 'black',
              border: 'none',
              color: 'white',
              fontSize: '12px',
            },
            handleStyles: {
              backgroundColor: '#EB7C0C',
              border: 'none',
              width: '10px',
              height: '10px',
            },
          },
          toolbar: resolvedToolbar,
        }}
      />
    </Container>
  )
})

Composed.whyDidYouRender = true

export default memo(Composed)
